import { useDockWide } from "./dockPreferences";
import type { ReactNode } from "react";

import { DoorUnseenSettleMark, UNSEEN_SETTLE_LABEL } from "./DoorUnseenSettleMark";
import { isElectron } from "~/env";
import { cn } from "~/lib/utils";
import { MACOS_TRAFFIC_LIGHTS_LEFT_INSET, useMacosTrafficLights } from "~/workspaceTitlebar";
import { EMPTY_WORLD_LIVENESS, type WorldLivenessRollups } from "~/neucleos/worldLiveness";
import { useWorldLivenessRollups } from "~/neucleos/useWorldLiveness";
import {
  useActorSeenCursorAdoption,
  useWorldSeenCursorStamp,
  useWorldUnseenSettleRollups,
} from "~/neucleos/useWorldUnseenSettle";
import {
  EMPTY_WORLD_UNSEEN_SETTLE,
  type WorldUnseenSettleRollups,
} from "~/neucleos/worldUnseenSettle";
import { enterLocal, useNeucleosWorldStore } from "~/neucleos/worldStore";
import { useWorldBrandMark } from "~/neucleos/useWorldBrandMark";
import { allModeActive, type NeucleosWorldSummary } from "~/neucleos/worldSelection";
import {
  ROOT_SESSION_STATUS_LABELS,
  rootSessionStatusFillClass,
  type RootSessionStatus,
} from "~/rootSessionStrip";
import { AgentStrip } from "./AgentStrip";
import { DeskSearchDoor } from "./DeskSearchDoor";
import { DeskNotifications } from "./DeskNotifications";
import { DeskMasthead } from "./DeskMasthead";
import { DeskUpdateButton } from "./DeskUpdateButton";
import { WorldSwitcher } from "./WorldSwitcher";

export function NeucleosWorldRail() {
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const currentWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const allSelected = useNeucleosWorldStore((state) => allModeActive(state.selection));
  const localSelected = useNeucleosWorldStore((state) => state.selection?.status === "local");
  const liveness = useWorldLivenessRollups();
  // NV5 — the rail is the always-mounted chrome, so it owns both halves of
  // unseen settle: standing in a door stamps its seen cursor, and the fold
  // over raw settle stamps reaches every backgrounded door below.
  // NV8 — and the Actor-level half: each entitled World's own cursor is
  // adopted through the grant door and merged in before the fold runs.
  useWorldSeenCursorStamp();
  useActorSeenCursorAdoption();
  const unseen = useWorldUnseenSettleRollups();
  // LG4 — the entered World's own mark, resolved through the brand fold and
  // the signed media lane; null keeps the estate wordmark.
  const brandMarkUrl = useWorldBrandMark(currentWorldKey);
  // FD4: the utility doors live on the DeskDock; FD-CAN-11 rolled the
  // Worlds into the switcher beside the masthead. Search is centered;
  // Notifications has one persistent seat at the titlebar's right end.
  return (
    <NeucleosWorldRailStrip
      worlds={worlds}
      brandMarkUrl={brandMarkUrl}
      currentWorldKey={currentWorldKey}
      localSelected={localSelected}
      signedIn={signedIn}
      allSelected={allSelected}
      liveness={liveness}
      unseen={unseen}
      agents={<AgentStrip />}
    />
  );
}

/**
 * The door's liveness dot: sessions in a backgrounded World keep running, so
 * its door carries the subtree's most urgent status. Quiet Worlds carry
 * nothing — the dot is a signal, not furniture (same law as the account
 * footer: healthy states stay unmarked).
 */
function DoorLivenessDot({ status }: { readonly status: RootSessionStatus | null }) {
  if (status === null) return null;
  return (
    <>
      <span
        aria-hidden="true"
        className={`size-1.5 shrink-0 rounded-full forced-colors:bg-[ButtonText] ${rootSessionStatusFillClass(status)}`}
      />
      <span className="sr-only">, {ROOT_SESSION_STATUS_LABELS[status]}</span>
    </>
  );
}

// The marker itself lives in DoorUnseenSettleMark.tsx (NV9: the switcher's
// rows wear the same word); the label re-export keeps this module the rail
// stories' one import for the door grammar.
export { UNSEEN_SETTLE_LABEL };

const DOOR_CLASS = {
  selected: "border-primary bg-primary font-medium text-primary-foreground",
  idle: "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
} as const;

/**
 * The explicit Local (ungoverned) mode control: the traditional harness,
 * chosen as an operating mode rather than implied by being signed out. Local
 * is not a World and never sits inside the Worlds nav (O24/W42) — it renders
 * as a separate control beside it, so Local is always one click away,
 * including from a World whose rooms refuse to read.
 */
export function NeucleosLocalDoor({
  selected,
  className,
  status = null,
  unseen = false,
}: {
  readonly selected: boolean;
  readonly className?: string;
  /** Most urgent live status among ungoverned threads; null renders no dot. */
  readonly status?: RootSessionStatus | null;
  /** NV5: an ungoverned settle the person has not stood in Local since. */
  readonly unseen?: boolean;
}) {
  const marked = unseen && !selected;
  return (
    <button
      type="button"
      // The current mode stays focusable (a disabled door would fall out of
      // the Tab order and hide its own state from keyboard users); activating
      // it is simply a no-op.
      aria-current={selected ? "true" : undefined}
      data-mode-door="local"
      data-mode-door-status={status ?? undefined}
      data-mode-door-unseen={marked ? "" : undefined}
      onClick={() => {
        if (selected) return;
        enterLocal();
      }}
      className={cn(
        "flex min-h-6 flex-none items-center gap-1.5 rounded-md border px-2 text-[11px] tracking-[0.08em]",
        selected ? cn(DOOR_CLASS.selected, "cursor-default") : DOOR_CLASS.idle,
        className,
      )}
      title={cn(
        selected ? "Local — ungoverned, current" : "Continue in Local (ungoverned)",
        status !== null && `· ${ROOT_SESSION_STATUS_LABELS[status]}`,
        marked && `· ${UNSEEN_SETTLE_LABEL}`,
      )}
    >
      Local
      <DoorLivenessDot status={status} />
      <DoorUnseenSettleMark marked={marked} />
    </button>
  );
}

export function NeucleosWorldRailStrip({
  worlds,
  currentWorldKey,
  brandMarkUrl = null,
  localSelected = false,
  signedIn = worlds.length > 1,
  allSelected = false,
  liveness = EMPTY_WORLD_LIVENESS,
  unseen = EMPTY_WORLD_UNSEEN_SETTLE,
  utilities,
  agents,
}: {
  readonly worlds: ReadonlyArray<NeucleosWorldSummary>;
  readonly currentWorldKey: string | null;
  /** The entered World's finalized mark URL (LG4); null wears the wordmark. */
  readonly brandMarkUrl?: string | null;
  readonly localSelected?: boolean;
  readonly signedIn?: boolean;
  /** The ALL stance: the whole entitlement fronted, no World filter applied. */
  readonly allSelected?: boolean;
  /** Live rollups for the doors; defaults to quiet so tests render bare. */
  readonly liveness?: WorldLivenessRollups;
  /** NV5 unseen-settle rollups; defaults to quiet so tests render bare. */
  readonly unseen?: WorldUnseenSettleRollups;
  /**
   * The shell utility cluster, seated as a fixed sibling AFTER the Worlds
   * nav: many Worlds scroll inside the nav while the utilities stay put,
   * and semantically the utilities are never Worlds.
   */
  readonly utilities?: ReactNode;
  /**
   * The agent strip (agent-strip phase 1): live sessions as rings beside
   * Search & commands, with the Inbox dropping down from it.
   */
  readonly agents?: ReactNode;
}) {
  // Signed out there is no WORLDS choice — but the shell chrome is not a
  // membership perk. The strip keeps its titlebar duties (the desktop drag
  // region), the estate face, the Local door it is running as, and the
  // utility doors; only the Worlds nav and the switcher — the things that
  // need memberships — wait for sign-in. Local-mode users deserve shell
  // chrome (2026-08-26; the null strip also removed the window's grab area).
  const showsMacosTrafficLights = useMacosTrafficLights();
  const [wide] = useDockWide();

  return (
    <div
      data-neucleos-world-rail=""
      data-neucleos-world-rail-mode={signedIn ? undefined : "signed-out"}
      className={cn(
        // FD-CAN-33 (DATED 2026-09-02): the design of record's header — 24px
        // side padding, NO rule beneath — now seated ABOVE the shell as the
        // window's titlebar row (--desk-header-height, the main process's
        // TITLEBAR_HEIGHT), riding the aurora; the shell begins under it.
        "ncl-reactbits grid h-[var(--desk-header-height)] w-full flex-none grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-6",
        // The titlebar: the grab area for moving the window (its buttons opt
        // out via the drag-region rules), and clear of the native controls
        // that share its row — the Windows/Linux overlay at the right (the
        // inset is viewport-measured; the rail's edge sits 14px inside it).
        isElectron && "drag-region wco:pr-[calc(var(--workspace-native-controls-inset)_-_14px)]",
      )}
      // The macOS traffic lights share the row at the left; the inset is
      // viewport-measured and the rail's edge sits 14px inside the window.
      style={
        showsMacosTrafficLights
          ? { paddingLeft: `calc(${MACOS_TRAFFIC_LIGHTS_LEFT_INSET} - 14px)` }
          : undefined
      }
    >
      {/* FD3 (D-FD2): the product masthead leads the window's topmost row —
          seated first and left-justified, after the macOS traffic lights that
          share the titlebar row, before every door. It is product
          identity, not a control: no border, no hit target, and inside the
          drag region it stays part of the grab area. The entered World's face
          moved onto its own door (the standalone WorldMark seat retired). */}
      {/* DATED 2026-09-03 (the principal: "center them so they are in the
          center of the full window"): the row is a three-column grid whose
          side columns are EQUAL (minmax(0,1fr) each), so the middle column
          is centred on the window itself — not on whatever room the
          masthead and switcher leave over. The traffic-light inset and the
          native-controls inset are padding on the row, so the centre sits
          on the padding box; the 14px stage edge is symmetric. */}
      <div className="flex min-w-0 items-center gap-2" data-world-rail-lead="">
        {/* RM-CAN-14: 8870c643's Local stand-in is gone — the dock carries the
            selector in Local again (53a4a655), so Local needs no second one here.
            The tight dock, which seats no selector, keeps this one in every stance. */}
        {signedIn && (!wide || (!currentWorldKey && !localSelected && !allSelected)) ? (
          <WorldSwitcher
            triggerContent={<DeskMasthead />}
            triggerClassName="h-auto rounded-sm p-0 hover:bg-transparent"
            worlds={worlds}
            currentWorldKey={currentWorldKey}
            localSelected={localSelected}
            allSelected={allSelected}
            brandMarkUrl={brandMarkUrl}
            liveness={liveness}
            unseen={unseen}
          />
        ) : (
          <DeskMasthead />
        )}
      </div>
      <div
        className="relative flex min-w-0 max-w-[min(48vw,640px)] items-center justify-center gap-2"
        data-world-rail-center=""
      >
        <DeskSearchDoor />
        {agents}
      </div>
      <div className="flex min-w-0 items-center justify-end gap-2" data-world-rail-trail="">
        {utilities}
        <DeskUpdateButton />
        <DeskNotifications />
      </div>
    </div>
  );
}
